import isEmpty from 'lodash/isEmpty';
import { JSX, useContext } from 'react';
import { SearchFilterContentTypes } from 'search-gateway-client';
import { ContentCollection } from 'stockblocks-client';

import { NavSearchContext } from '../../context/NavSearchContext';
import useMedia from '../../utils/useMedia';
import { SearchBarVariant } from '../NavSearchElements/searchUtils';
import AutoSuggestDropdown, { AutoSuggestDropdownProps } from './AutoSuggestDropdown';
import CollectionsSuggestion, { CollectionsSuggestionProps } from './CollectionsSuggestion';
import SearchHistory from './SearchHistory';
import { NullStateSuggestionTypes } from './constants';

export type NullSuggestions = {
    latestCollections?: ContentCollection[];
    trendingCollections?: ContentCollection[];
};

export interface NullSearchDropdownProps {
    showSuggestions?: boolean;
    suggestions?: NullSuggestions;
    autoSuggestDropdownProps?: Omit<AutoSuggestDropdownProps, 'show'>;
    latestCollectionsSuggestionProps?: Omit<CollectionsSuggestionProps, 'collections'>;
    trendingCollectionsSuggestionProps?: Omit<CollectionsSuggestionProps, 'collections'>;
    contentType: SearchFilterContentTypes;
    useNewSearchUI?: boolean;
    useWarpSearchUI?: boolean;
}

const searchBarVariantStyle = {
    [SearchBarVariant.ProminentSearch]: 'flex flex-col py-4 px-6 gap-y-7',
    [SearchBarVariant.ProminentSearchHistoryExperiment]: 'flex flex-col py-4 px-6 gap-y-7',
};

const NullSearchDropdown = ({
    showSuggestions = false,
    suggestions = {
        latestCollections: [],
        trendingCollections: [],
    },
    autoSuggestDropdownProps = {},
    latestCollectionsSuggestionProps = {},
    trendingCollectionsSuggestionProps = {},
    contentType,
    useNewSearchUI,
    useWarpSearchUI = false,
}: NullSearchDropdownProps): JSX.Element => {
    const isLargeScreen = useMedia('(min-width: 1024px)', true);
    const numberItemsToShow = useNewSearchUI && !isLargeScreen ? 2 : 3;
    const context = useContext(NavSearchContext);
    const { searchBarVariant } = context;

    return (
        <AutoSuggestDropdown
            show={showSuggestions}
            className={
                searchBarVariantStyle[searchBarVariant as keyof typeof searchBarVariantStyle] ??
                'flex flex-col py-4 px-6 gap-y-7 mt-2.5 -ml-2.5'
            }
            {...autoSuggestDropdownProps}
            useNewSearchUI={useNewSearchUI}
            useWarpSearchUI={useWarpSearchUI}
        >
            <SearchHistory contentType={contentType}>
                {!isEmpty(suggestions?.latestCollections) && (
                    <CollectionsSuggestion
                        collections={suggestions.latestCollections}
                        title="Latest Collections"
                        numberItemsToShow={numberItemsToShow}
                        showSeeMoreLink={false}
                        className="p-0"
                        titleClassName="w-1/2"
                        getTelemetryString={(routeUri) => `typeahead.null.collectionNew.${routeUri}`}
                        idLabel={`${NullStateSuggestionTypes.latestCollections}`}
                        {...latestCollectionsSuggestionProps}
                    />
                )}
                {!isEmpty(suggestions?.trendingCollections) && (
                    <CollectionsSuggestion
                        collections={suggestions.trendingCollections}
                        title="Trending Collections"
                        numberItemsToShow={numberItemsToShow}
                        className="p-0 space-y-3"
                        titleClassName="w-1/2"
                        getTelemetryString={(routeUri) => `typeahead.null.collectionTrending.${routeUri}`}
                        idLabel={`${NullStateSuggestionTypes.trendingCollections}`}
                        {...trendingCollectionsSuggestionProps}
                    />
                )}
            </SearchHistory>
        </AutoSuggestDropdown>
    );
};

export default NullSearchDropdown;
